var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineStore } from "pinia";
import ComponentEndpoints from "src/shared/api/campaign/component/ComponentEndpoints";
import { toPromise } from "src/shared/api/promise";
import _ from "underscore";
import { reactive } from "vue";
import Component from "./model/Component";
import { campaignHasBeenUpdated } from "src/shared/data-loading/outbound/campaign/emitters/campaignHasBeenUpdated";
import { componentHasBeenUpdated } from "src/shared/data-loading/outbound/component/emitters/componentHasBeenUpdated";
import { campaignComponentChangeEvent, campaignComponentDeletedEvent, campaignComponentListChangeEvent, campaignComponentUpdatedEvent, } from "../event-bus/campaign-component";
import CampaignComponentEndpoints from "src/shared/api/campaign/component/CampaignComponentEndpoints";
import { useBuiltComponent } from "../component-built/composables";
import { until } from "@vueuse/core";
import BatchVariableEndpoints from "src/shared/api/campaign/component/variable/batch/BatchVariableEndpoints";
import CampaignComponentVariableEndpoints from "src/shared/api/campaign/component/variable/CampaignComponentVariableEndpoints";
import { doCampaignOperation } from "../control-flow/campaign-operation-queue";
import CampaignComponentSettingEndpoints from "src/shared/api/campaign/component/setting/CampaignComponentSettingEndpoints";
import { createComponentSettingUpdateRequest } from "./createComponentSettingUpdateRequest";
import { fetchAll } from "src/shared/store/rest/fetch-all";
const fetchDuplicatableComponentsFunction = (parameters) => __awaiter(void 0, void 0, void 0, function* () {
    return yield toPromise(ComponentEndpoints.listDuplicatableComponents(parameters));
});
const fetchComponentsFunction = (parameters) => __awaiter(void 0, void 0, void 0, function* () {
    return yield toPromise(ComponentEndpoints.list(parameters));
});
const convertToBatchUpdateRequest = (variables) => {
    const request = {
        variables: {},
    };
    variables.forEach((variable) => {
        request.variables[variable.getName()] =
            variable.serializeToUpdateRequest();
    });
    return request;
};
const loadComponentUpdateHandle = (componentId) => __awaiter(void 0, void 0, void 0, function* () {
    const { builtComponent } = useBuiltComponent(componentId);
    yield until(builtComponent).not.toBeUndefined();
    if (builtComponent.value === undefined) {
        throw new Error("Component not found");
    }
    return {
        componentId: builtComponent.value.id,
        campaignId: builtComponent.value.campaign_id,
    };
});
const convertToVariableCreateRequests = (variables) => {
    return _.map(variables, (variable) => {
        return {
            name: variable.name,
            type: variable.type,
            values: variable.values,
            source: variable.source,
            description: variable.description,
            tags: variable.tags,
            priority: variable.priority,
        };
    });
};
const convertToVariableCreateRequestsV2 = (variables) => {
    // TODO - Fix in https://extole.atlassian.net/browse/ENG-23527, Sockets must be omitted from the request for now
    return _.chain(variables)
        .filter((variable) => variable.getType() !== "MULTI_SOCKET" &&
        variable.getType() !== "SOCKET")
        .map((variable) => {
        return variable.serializeToCreateRequest();
    })
        .value();
};
const convertToComponentDuplicateRequestV2 = (parameters) => {
    return Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, (parameters.targetCampaignId && {
        target_campaign_id: parameters.targetCampaignId,
    })), (parameters.componentName && {
        component_name: parameters.componentName,
    })), (parameters.targetComponentId && {
        component_ids: [parameters.targetComponentId],
    })), (parameters.tags &&
        !_.isEmpty(parameters.tags) && {
        tags: parameters.tags,
    })), (parameters.component_ids &&
        !_.isEmpty(parameters.component_ids) && {
        component_ids: parameters.component_ids,
    })), (parameters.variables &&
        !_.isEmpty(parameters.variables) && {
        variables: convertToVariableCreateRequestsV2(parameters.variables),
    }));
};
const convertToComponentCreateRequest = (parameters) => {
    return Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({ name: parameters.name, type: parameters.type, upload_version: parameters.upload_version }, (parameters.description !== null &&
        !_.isEmpty(parameters.description) && {
        description: parameters.description,
    })), (parameters.tags !== null &&
        !_.isEmpty(parameters.tags) && {
        tags: parameters.tags,
    })), (parameters.variables !== null &&
        !_.isEmpty(parameters.variables) && {
        variables: convertToVariableCreateRequests(parameters.variables),
    })), (parameters.component_ids &&
        !_.isEmpty(parameters.component_ids) && {
        component_ids: parameters.component_ids,
    })), (parameters.facets !== null &&
        !_.isEmpty(parameters.facets) && {
        facets: parameters.facets,
    }));
};
export const useComponentStoreV2 = defineStore("componentStoreV2", () => {
    const state = reactive({
        components: {},
        duplicatableComponents: {},
    });
    const setComponents = (componentResponses) => {
        return _.map(componentResponses, (response) => {
            const component = new Component(response);
            state.components[component.id] = component;
            campaignComponentUpdatedEvent.emit({
                campaignId: component.campaign_id,
                componentId: component.id,
            });
            return component;
        });
    };
    const setDuplicatableComponents = (componentResponses) => {
        return _.map(componentResponses, (response) => {
            const component = new Component(response);
            state.duplicatableComponents[component.id] = component;
            return component;
        });
    };
    const fetchComponents = (filter = {}) => __awaiter(void 0, void 0, void 0, function* () {
        const hasNoLimitSpecified = !filter.limit;
        if (hasNoLimitSpecified) {
            const allComponents = yield fetchAll(fetchComponentsFunction, filter);
            return setComponents(allComponents);
        }
        else {
            const response = yield toPromise(ComponentEndpoints.list(filter));
            return setComponents(response);
        }
    });
    const fetchDuplicatableComponents = (filter = {}) => __awaiter(void 0, void 0, void 0, function* () {
        const hasNoLimitSpecified = !filter.limit;
        if (hasNoLimitSpecified) {
            const allComponents = yield fetchAll(fetchDuplicatableComponentsFunction, filter);
            return setDuplicatableComponents(allComponents);
        }
        else {
            const response = yield toPromise(ComponentEndpoints.listDuplicatableComponents(filter));
            return setDuplicatableComponents(response);
        }
    });
    const fetchComponent = (componentId) => __awaiter(void 0, void 0, void 0, function* () {
        const response = yield toPromise(ComponentEndpoints.get(componentId));
        const [component] = setComponents([response]);
        return component;
    });
    const getComponents = () => {
        return _.values(state.components);
    };
    const getComponent = (id) => {
        return state.components[id];
    };
    const getDuplicatableComponents = () => {
        return _.values(state.duplicatableComponents);
    };
    const getDuplicatableComponent = (id) => {
        return state.duplicatableComponents[id];
    };
    const addChildComponent = (parameters) => __awaiter(void 0, void 0, void 0, function* () {
        let idOfNewComponent;
        let campaignIdOfNewComponent;
        if (parameters.socketName &&
            parameters.targetCampaignId &&
            parameters.targetComponentId) {
            const socketDuplicationResponse = yield toPromise(CampaignComponentSettingEndpoints.addComponent(parameters.targetCampaignId, parameters.targetComponentId, parameters.socketName, Object.assign(Object.assign({ source_component_id: parameters.sourceComponentId }, (parameters.displayName && {
                component_display_name: parameters.displayName,
            })), (parameters.settings && {
                settings: parameters.settings,
            }))));
            idOfNewComponent = socketDuplicationResponse.id;
            campaignIdOfNewComponent = parameters.targetCampaignId;
        }
        else {
            const parentComponentId = parameters.targetComponentId
                ? [parameters.targetComponentId]
                : [];
            const componentDuplicationResponse = yield toPromise(ComponentEndpoints.duplicate(parameters.sourceComponentId, Object.assign(Object.assign({ component_ids: parentComponentId }, (parameters.targetCampaignId && {
                target_campaign_id: parameters.targetCampaignId,
            })), (parameters.displayName && {
                component_display_name: parameters.displayName,
            }))));
            idOfNewComponent = componentDuplicationResponse.id;
            campaignIdOfNewComponent = componentDuplicationResponse.campaign_id;
        }
        componentHasBeenUpdated(idOfNewComponent);
        campaignComponentListChangeEvent.emit({
            campaignId: campaignIdOfNewComponent,
        });
        return {
            campaignId: campaignIdOfNewComponent,
            componentId: idOfNewComponent,
        };
    });
    const duplicateComponentV2 = (parameters) => __awaiter(void 0, void 0, void 0, function* () {
        const response = yield toPromise(ComponentEndpoints.duplicate(parameters.sourceComponentId, convertToComponentDuplicateRequestV2(parameters)));
        componentHasBeenUpdated(response.id);
        campaignComponentListChangeEvent.emit({
            campaignId: response.campaign_id,
        });
        return {
            campaignId: response.campaign_id,
            componentId: response.id,
        };
    });
    const removeComponentFromSocket = (parameters) => __awaiter(void 0, void 0, void 0, function* () {
        yield toPromise(CampaignComponentSettingEndpoints.removeComponent(parameters.campaignId, parameters.componentId, parameters.settingName, parameters.targetComponentId));
        // TODO - Replace this with componentHasBeenDeleted once we have it
        campaignHasBeenUpdated(parameters.campaignId);
        campaignComponentListChangeEvent.emit({
            campaignId: parameters.campaignId,
        });
    });
    const createComponent = (campaignId, parameters) => __awaiter(void 0, void 0, void 0, function* () {
        const request = convertToComponentCreateRequest(parameters);
        try {
            const response = yield toPromise(CampaignComponentEndpoints.create(campaignId, request));
            componentHasBeenUpdated(response.id);
            campaignComponentListChangeEvent.emit({
                campaignId: campaignId,
            });
            return {
                componentId: response.id,
            };
        }
        catch (error) {
            console.log(error);
            throw new Error("Failed to create component");
        }
    });
    const updateComponent = (campaignId, componentId, mutable) => __awaiter(void 0, void 0, void 0, function* () {
        const request = mutable.serializeToUpdateRequest();
        yield toPromise(CampaignComponentEndpoints.update(campaignId, componentId, request));
        componentHasBeenUpdated(componentId);
        campaignComponentListChangeEvent.emit({
            campaignId: campaignId,
        });
    });
    const deleteComponent = (campaignId, componentId) => __awaiter(void 0, void 0, void 0, function* () {
        const deletedComponent = getComponent(componentId);
        yield toPromise(CampaignComponentEndpoints.deleteCampaignComponent(campaignId, componentId));
        if (!(deletedComponent === null || deletedComponent === void 0 ? void 0 : deletedComponent.isRoot())) {
            // TODO - Replace this with componentHasBeenDeleted once we have it
            campaignHasBeenUpdated(campaignId);
        }
        campaignComponentDeletedEvent.emit({
            campaignId: campaignId,
            componentId: componentId,
        });
    });
    const createComponentSetting = (componentId, name, setting) => __awaiter(void 0, void 0, void 0, function* () {
        const { campaignId } = yield loadComponentUpdateHandle(componentId);
        yield doCampaignOperation(campaignId, () => __awaiter(void 0, void 0, void 0, function* () {
            setting.name = name;
            yield toPromise(CampaignComponentSettingEndpoints.create(campaignId, componentId, setting));
            componentHasBeenUpdated(componentId);
        }));
    });
    const updateComponentSetting = (componentId, settingName, setting) => __awaiter(void 0, void 0, void 0, function* () {
        const { campaignId } = yield loadComponentUpdateHandle(componentId);
        yield doCampaignOperation(campaignId, () => __awaiter(void 0, void 0, void 0, function* () {
            const request = createComponentSettingUpdateRequest(setting);
            request.name = settingName;
            yield toPromise(CampaignComponentSettingEndpoints.update(campaignId, componentId, settingName, request));
            componentHasBeenUpdated(componentId);
        }));
    });
    const createComponentVariable = (componentId, variableName, variable) => __awaiter(void 0, void 0, void 0, function* () {
        const { campaignId } = yield loadComponentUpdateHandle(componentId);
        const request = variable.serializeToCreateRequest();
        request.name = variableName;
        yield doCampaignOperation(campaignId, () => __awaiter(void 0, void 0, void 0, function* () {
            yield CampaignComponentVariableEndpoints.create(campaignId, componentId, request);
            componentHasBeenUpdated(componentId);
        }));
    });
    const createComponentVariableSimple = (componentId, variableName, type) => __awaiter(void 0, void 0, void 0, function* () {
        const { campaignId } = yield loadComponentUpdateHandle(componentId);
        yield doCampaignOperation(campaignId, () => __awaiter(void 0, void 0, void 0, function* () {
            yield CampaignComponentVariableEndpoints.create(campaignId, componentId, {
                name: variableName,
                type: type,
                source: "INHERITED",
            });
            componentHasBeenUpdated(componentId);
        }));
    });
    const batchUpdateComponentVariables = (componentId, variables) => __awaiter(void 0, void 0, void 0, function* () {
        const { campaignId } = yield loadComponentUpdateHandle(componentId);
        const request = convertToBatchUpdateRequest(variables);
        yield doCampaignOperation(campaignId, () => __awaiter(void 0, void 0, void 0, function* () {
            yield toPromise(BatchVariableEndpoints.update(campaignId, componentId, request));
            componentHasBeenUpdated(componentId);
        }));
    });
    const deleteComponentVariable = (componentId, variableName) => __awaiter(void 0, void 0, void 0, function* () {
        const { campaignId } = yield loadComponentUpdateHandle(componentId);
        yield doCampaignOperation(campaignId, () => __awaiter(void 0, void 0, void 0, function* () {
            yield CampaignComponentVariableEndpoints.destroy(campaignId, componentId, variableName);
            componentHasBeenUpdated(componentId);
        }));
    });
    campaignComponentChangeEvent.on((event) => {
        fetchComponent(event.componentId);
    });
    campaignComponentDeletedEvent.on((payload) => {
        delete state.components[payload.componentId];
    });
    return {
        state,
        fetchComponent,
        fetchComponents,
        getComponents,
        getComponent,
        addChildComponent,
        removeComponentFromSocket,
        updateComponent,
        createComponent,
        deleteComponent,
        batchUpdateComponentVariables,
        createComponentSetting,
        updateComponentSetting,
        createComponentVariable,
        createComponentVariableSimple,
        deleteComponentVariable,
        duplicateComponentV2,
        fetchDuplicatableComponents,
        getDuplicatableComponents,
        getDuplicatableComponent,
    };
});
